import React from 'react';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();

    const sections = [
        {
            title: 'Products',
            links: [
                { label: 'Chart Patterns', href: '/patterns' },
                { label: 'Pattern Tracker', href: '/pattern-tracker' },
                { label: 'Track Record', href: '/track-record' },
                { label: 'Reports', href: '/reports' },
                { label: 'Pricing', href: '/pricing' },
            ],
        },
        {
            title: 'Learning Resources',
            links: [
                { label: 'All Guides', href: '/learn' },
                { label: 'Chart Patterns Guide', href: '/learn/patterns' },
                { label: 'Smart Money Concepts (SMC)', href: '/learn/smc' },
                { label: 'Convergence Signals', href: '/learn/convergence' },
                { label: 'MC/TVL Ratio', href: '/learn/mc-tvl' },
                { label: 'P/S Ratio', href: '/learn/ps-ratio' },
                { label: 'Head and Shoulders', href: '/learn/patterns/head-and-shoulders' },
                { label: 'Double Top and Bottom', href: '/learn/patterns/double-top-double-bottom' },
                { label: 'Engulfing Patterns', href: '/learn/patterns/bullish-bearish-engulfing' },
                { label: 'Cup and Handle', href: '/learn/patterns/cup-and-handle' },
                { label: 'Fair Value Gap', href: '/learn/patterns/fair-value-gap' },
                { label: 'Order Block', href: '/learn/patterns/order-block' },
                { label: 'Ascending Triangle', href: '/learn/patterns/ascending-triangle' },
                { label: 'Descending Triangle', href: '/learn/patterns/descending-triangle' },
                { label: 'Symmetrical Triangle', href: '/learn/patterns/symmetrical-triangle' },
            ],
        },
        {
            title: 'About',
            links: [
                { label: 'About Us', href: '/about' },
                { label: 'Help Center', href: '/help' },
                { label: 'Contact Us', href: '/contact' },
                { label: 'Privacy Policy', href: '/privacy' },
                { label: 'Terms of Service', href: '/terms' },
            ],
        },
    ];

    return (
        <footer className="relative mt-20 border-t border-white/[0.06] bg-[#09090b] overflow-hidden">
            {/* Top hairline */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/12 to-transparent" />
            {/* Soft ambient glows */}
            <div className="absolute top-0 left-1/4 w-80 h-80 bg-white/[0.015] blur-[100px] rounded-full -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-white/[0.01] blur-[100px] rounded-full translate-y-1/2 pointer-events-none" />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 xl:px-12 py-14 sm:py-16 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
                    {/* Brand Section */}
                    <div className="space-y-6">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-nebula">
                                <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm5-18v4h3V3h-3z" />
                                </svg>
                            </div>
                            <span className="text-xl font-bold text-white tracking-tight">
                                LuminaAnalysis
                            </span>
                        </div>
                        <p className="text-dark-textSecondary text-sm leading-relaxed max-w-xs">
                            AI-Powered Crypto Metric Analysis Platform
                        </p>
                        <div className="flex space-x-4">
                            <a
                                href="https://x.com/Waylon122121"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Follow LuminaAnalysis on X"
                                className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-dark-textMuted hover:text-white hover:border-white/30 hover:bg-white/5 transition-all duration-200"
                            >
                                {/* X (formerly Twitter) logo */}
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* Links Sections */}
                    {sections.map((section) => (
                        <div key={section.title}>
                            <h3 className="text-white font-semibold mb-6 tracking-wide uppercase text-xs">
                                {section.title}
                            </h3>
                            <ul className="space-y-4">
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <Link
                                            to={link.href}
                                            className="text-dark-textMuted text-sm hover:text-white transition-colors duration-200 block"
                                        >
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Financial Disclaimer */}
                <div className="mt-12 p-4 rounded-xl border border-amber-500/15 bg-amber-500/5 text-xs text-zinc-400 leading-relaxed">
                    <span className="font-bold text-amber-500/80 mr-1">Not Financial Advice.</span>
                    LuminaAnalysis provides AI-generated pattern analysis for informational and educational purposes only. Nothing on this platform constitutes financial, investment, or trading advice. Always conduct your own research and consult a licensed financial advisor before making any investment decisions. Crypto assets are highly volatile and you may lose all capital invested.
                </div>

                {/* Data Sources Attribution */}
                <div className="mt-8 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0 text-dark-textMuted text-xs">
                    <div className="flex items-center space-x-6 grayscale opacity-90 hover:grayscale-0 hover:opacity-100 transition-all duration-300">
                        <span className="font-medium uppercase tracking-widest text-[10px] mr-2">Powered by</span>
                        <div className="flex items-center space-x-4">
                            <span>Binance</span>
                            <span className="w-1 h-1 bg-white/20 rounded-full"></span>
                            <span>CoinGecko</span>
                            <span className="w-1 h-1 bg-white/20 rounded-full"></span>
                            <span>DeFiLlama</span>
                        </div>
                    </div>

                    <div className="text-dark-textMuted">
                        © {currentYear} LuminaAnalysis
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
